import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Search, Building2, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface School {
    id: number;
    school_id: number;
    name: string;
    slug: string;
    city: string;
    area: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const MAX_SCHOOLS = 20;

const AppearsInSchools: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [availableSchools, setAvailableSchools] = useState<School[]>([]);
    const [isLoadingSchools, setIsLoadingSchools] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50,
    });

    const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
    const [appearsInSchools, setAppearsInSchools] = useState<School[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const fetchAvailableSchools = async (page: number = 1) => {
        try {
            setIsLoadingSchools(true);
            const response = await apiRequest(
                `/related-schools/available?page=${page}&limit=50&search=${encodeURIComponent(searchTerm)}&exclude_id=${id}`
            );
            if (response.success) {
                setAvailableSchools(response.data.schools || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch schools', 'error');
            }
        } catch (error) {
            console.error('Error fetching schools:', error);
            showToast('Failed to fetch schools', 'error');
        } finally {
            setIsLoadingSchools(false);
        }
    };

    const fetchAppearsInSchools = async () => {
        try {
            const response = await apiRequest(`/related-schools/${id}/appears-in`);
            if (response.success) {
                const schools = response.data.appearsInSchools || [];
                setAppearsInSchools(schools);
                setSelectedSchoolIds(schools.map((s: School) => s.id));
            } else {
                showToast('Failed to fetch appears-in schools', 'error');
            }
        } catch (error) {
            console.error('Error fetching appears-in schools:', error);
            showToast('Failed to fetch appears-in schools', 'error');
        }
    };

    useEffect(() => {
        fetchAvailableSchools(1);
        fetchAppearsInSchools();
    }, [id]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAvailableSchools(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleSchoolToggle = (schoolId: number) => {
        setSelectedSchoolIds(prev => {
            if (prev.includes(schoolId)) {
                return prev.filter(sid => sid !== schoolId);
            }
            if (prev.length >= MAX_SCHOOLS) {
                showToast(`You can only select up to ${MAX_SCHOOLS} schools`, 'error');
                return prev;
            }
            return [...prev, schoolId];
        });
    };

    const handleRemoveSelected = (schoolId: number) => {
        setSelectedSchoolIds(prev => prev.filter(sid => sid !== schoolId));
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const response = await apiRequest(`/related-schools/${id}/appears-in`, {
                method: 'POST',
                body: JSON.stringify({ school_ids: selectedSchoolIds }),
            });
            if (response.success) {
                showToast('Saved successfully', 'success');
                fetchAppearsInSchools();
            } else {
                showToast('Failed to save', 'error');
            }
        } catch (error) {
            console.error('Error saving:', error);
            showToast('Failed to save', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const getSelectedSchoolDetails = () => {
        const allSchools = [...appearsInSchools, ...availableSchools];
        return selectedSchoolIds.map(schoolId => {
            const school = allSchools.find(s => s.id === schoolId);
            return school || { id: schoolId, school_id: schoolId, name: `School #${schoolId}`, slug: '', city: '', area: '' };
        });
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Appears In Schools</h1>
                            <p className="text-xs text-gray-500">Choose schools where this school will appear as a related school</p>
                        </div>
                    </div>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left — Available Schools */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4">Available Schools</h2>

                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search schools..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {isLoadingSchools ? (
                                <p className="text-sm text-gray-500 text-center py-4">Loading schools...</p>
                            ) : availableSchools.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No schools found</p>
                            ) : (
                                availableSchools.map((school) => {
                                    const isSelected = selectedSchoolIds.includes(school.id);
                                    return (
                                        <div
                                            key={school.id}
                                            onClick={() => handleSchoolToggle(school.id)}
                                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                isSelected
                                                    ? 'bg-blue-50 border-blue-300'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                                                isSelected ? 'bg-brandColor border-brandColor' : 'border-gray-300'
                                            }`}>
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{school.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    ID: {school.school_id || school.id}
                                                    {school.area && ` • ${school.area}`}
                                                    {school.city && school.city !== school.area && `, ${school.city}`}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <span className="text-xs text-gray-500">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableSchools(pagination.currentPage - 1)}
                                        disabled={pagination.currentPage === 1}
                                    >
                                        <ChevronLeft size={14} />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableSchools(pagination.currentPage + 1)}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                    >
                                        <ChevronRight size={14} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right — Selected Schools */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            Selected Schools ({selectedSchoolIds.length}/{MAX_SCHOOLS})
                        </h2>

                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {selectedSchoolIds.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No schools selected</p>
                            ) : (
                                getSelectedSchoolDetails().map((school) => (
                                    <div
                                        key={school.id}
                                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{school.name}</p>
                                            <p className="text-xs text-gray-500">
                                                ID: {school.school_id || school.id}
                                                {school.area && ` • ${school.area}`}
                                                {school.city && school.city !== school.area && `, ${school.city}`}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSelected(school.id)}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppearsInSchools;
